import React, { FC } from "react";

import LargeSpinner from "@Components/shared/LargeSpinner";
import { IFeedbackInstanceDetails } from "@Models/IFeedbackInstanceDetails";
import { IFeedbackTemplateDetails, ITemplateField } from "@Models/IFeedbackTemplateDetails";
import { prettifyName } from "@Utils";
import { FrownFilled, MehFilled, SmileFilled } from "@ant-design/icons";
import { Divider, Rate, Typography } from "antd";

import "@Styles/components/designer.scss";

const { Title, Text } = Typography;

interface IProps {
    description: IFeedbackTemplateDetails["description"];
    feedback: IFeedbackInstanceDetails;
    fields: ITemplateField[];
    isLoading?: boolean;
    name: IFeedbackTemplateDetails["name"];
}

type IFeedbackValue = IFeedbackTemplateDetails["fields"][number] & { value: string };

const formatValue = ({ options, type, value }: IFeedbackValue) => {
    switch (type) {
        case "text":
            return value === "undefined" ? "" : value;
        case "option":
            return value === "undefined" ? "" : value;

        case "textarea":
            return <span style={{ whiteSpace: "break-spaces" }}>{value === "undefined" ? "" : value}</span>;

        case "numeric":
            const isPercentage = options[2] === "true";
            return value === "undefined" ? "" : `${value}${isPercentage ? "%" : ""}`;

        case "boolean":
            return JSON.parse(value) ? "Yes" : "No";

        case "likert":
            switch (options[5]) {
                default: // Options - radio buttons
                    return options[value];

                case "2": // Rating
                    const ratings = options.slice(0, 5);
                    return <Rate disabled={true} tooltips={ratings} value={parseInt(value, 10)} />;

                case "3": // Smileys
                    const smileys = options.slice(0, 5);
                    const customIcons: Record<number, React.ReactNode> = {
                        1: <FrownFilled className={"smiley1"} />,
                        2: <FrownFilled className={"smiley2"} />,
                        3: <MehFilled className={"smiley3"} />,
                        4: <SmileFilled className={"smiley4"} />,
                        5: <SmileFilled className={"smiley5"} />,
                    };
                    return (
                        <Rate
                            disabled={true}
                            tooltips={smileys}
                            character={({ index }: { index: number }) => customIcons[index + 1]}
                            value={parseInt(value, 10)}
                        />
                    );
            }

        default:
            return value;
    }
};

const Feedback: FC<IProps> = ({ name, description, isLoading = false, feedback, fields }) => {
    const { formData } = feedback;

    return (
        <section>
            {isLoading && <LargeSpinner />}
            {!isLoading && (
                <div className="template-designer-item-list">
                    <Text type="secondary">{name}</Text>
                    <Title level={5} className="card-stat">
                        {description}
                    </Title>
                    <Divider />
                    {formData?.map(({ id, value }, index) => {
                        const field = fields[index];
                        return (
                            <>
                                <Text type="secondary">{field?.name || prettifyName(id)}</Text>
                                <Title level={5}>{formatValue({ ...field, value })} </Title>
                                <Divider />
                            </>
                        );
                    })}
                </div>
            )}
        </section>
    );
};

export default Feedback;
